import type { IconName } from '../components/Icon';
import { UserRole } from '../types';
import type { User } from '../types';
// Workshops with «الواجهة الجديدة للورشة» (user.workshopUiV2): their nav comes from workshop/nav.ts.
import { isWorkshopV2, workshopNav, workshopBottomTabs } from '../workshop/nav';
import type { WsNavBadgeKind } from '../workshop/nav';

/**
 * Every place a signed-in person can go, per role — the phone «المزيد» sheet reads this.
 *
 * Mirrors the desktop rail in components/Navbar.tsx (the complete list; the old phone drawer
 * had drifted and was missing ~10 entries). When a page is added to the rail, add it here too.
 */
export interface NavItem {
  to: string;
  /** i18n key, with a fallback label for keys some locales lack. */
  labelKey: string;
  fallback?: string;
  icon: IconName;
}

export interface NavGroup {
  /** i18n key of the section title; null = no title (the top block). */
  titleKey: string | null;
  items: NavItem[];
}

const item = (to: string, labelKey: string, icon: IconName, fallback?: string): NavItem => ({ to, labelKey, icon, fallback });

const CATALOG: NavItem[] = [
  item('/parts-catalog', 'partsCatalog.title', 'database', 'كتالوج القطع الأصلية'),
  item('/parts-catalog/fitment', 'partsCatalog.fitment.navTitle', 'wrench', 'توافق القطع'),
  item('/parts-catalog/cross-make', 'partsCatalog.crossMake.navTitle', 'refresh', 'مرادفات الماركات'),
];

export const isReadOnlyAdmin = (u: User | null | undefined) => !!u && u.role === UserRole.ADMIN && !!u.readOnly;

export function navGroupsFor(user: User | null | undefined): NavGroup[] {
  if (!user) return [];
  const readOnly = isReadOnlyAdmin(user);
  const catalogOn =
    user.role === UserRole.ADMIN || user.role === UserRole.SUPER_ADMIN || user.role === UserRole.AGENT ||
    (user.role === UserRole.WORKSHOP && user.partsCatalogEnabled !== false);

  if (readOnly) {
    return [{ titleKey: null, items: [item('/', 'common.dashboard', 'dashboard'), item('/orders', 'common.orders', 'orders'), item('/bonus', 'common.bonus', 'gift', 'Bonuses')] }];
  }

  switch (user.role) {
    case UserRole.WORKSHOP:
      if (isWorkshopV2(user)) {
        return workshopNav(user).map((g) => ({
          titleKey: g.titleKey,
          items: g.items.map((i) => ({ to: i.to, labelKey: i.labelKey, icon: i.icon, fallback: i.fallback })),
        }));
      }
      return [
        {
          titleKey: null,
          items: [
            item('/', 'common.dashboard', 'dashboard'),
            item('/orders', 'common.orders', 'orders'),
            item('/orders/create', 'common.createOrder', 'plus'),
            item('/assistant', 'common.aiAssistant', 'robot', 'المساعد'),
            item('/job-cards', 'common.jobCards', 'clipboard', 'Job Cards'),
            item('/feedbacks', 'common.feedbacks', 'star', 'Feedbacks'),
            item('/bonus', 'common.bonus', 'gift', 'Bonuses'),
            ...(user.workshopType === 'PRIVATE' ? [item('/my-cash', 'common.myCash', 'money', 'صندوق العهدة النقدية')] : []),
          ],
        },
        ...(catalogOn ? [{ titleKey: 'mobile.catalog', items: CATALOG }] : []),
      ];

    case UserRole.AGENT:
      return [
        {
          titleKey: null,
          items: [
            item('/', 'common.dashboard', 'dashboard'),
            item('/orders', 'common.orders', 'orders'),
            item('/orders/create', 'common.createOrder', 'plus'),
            item('/bonus', 'common.bonus', 'gift', 'Bonuses'),
          ],
        },
        { titleKey: 'mobile.catalog', items: CATALOG },
      ];

    case UserRole.SUPPLIER:
      return [
        {
          titleKey: null,
          items: [
            item('/', 'common.dashboard', 'dashboard'),
            item('/orders', 'common.orders', 'orders'),
            item('/bids', 'common.myBids', 'gavel'),
            item('/supplier/labour-links', 'common.labourLinks', 'wrench'),
            item('/bonus', 'common.bonus', 'gift', 'Bonuses'),
            item('/supplier/profile', 'common.profile', 'user', 'Profile'),
          ],
        },
      ];

    case UserRole.ADMIN:
    case UserRole.SUPER_ADMIN: {
      const sa = user.role === UserRole.SUPER_ADMIN;
      const groups: NavGroup[] = [
        {
          titleKey: null,
          items: [
            item('/', 'common.dashboard', 'dashboard'),
            item('/orders', 'common.orders', 'orders'),
            item('/orders/create', 'common.createOrder', 'plus'),
            item('/job-cards', 'common.jobCards', 'clipboard', 'Job Cards'),
            item('/feedbacks', 'common.feedbacks', 'star', 'Feedbacks'),
          ],
        },
        {
          titleKey: 'navGroups.operations',
          items: [
            item('/admin/delivery', 'delivery.navTitle', 'truck', 'لوحة التسليم'),
            item('/admin/live-bays', 'common.liveBays', 'bay', 'Live Bays'),
            item('/admin/inventory', 'common.inventory', 'box', 'Inventory'),
            item('/admin/technicians', 'common.technicians', 'wrench', 'Technicians'),
            item('/supplier/labour-links', 'common.labourLinks', 'money'),
          ],
        },
        {
          titleKey: 'navGroups.invoicing',
          items: [
            item('/admin/books', 'common.books', 'book', 'الحسابات'),
            item('/admin/purchase-invoices', 'common.purchaseInvoices', 'invoice'),
            item('/admin/quote-leads', 'quoteLeads.title', 'lead'),
            item('/admin/reconciliation', 'common.reconciliation', 'reconcile', 'تسوية المدفوعات'),
            ...(sa
              ? [
                  item('/admin/supplier-reconciliation', 'supplierRecon.navTitle', 'reconcile', 'تسوية حسابات الموردين'),
                  item('/admin/expense-audit', 'common.expenseAudit', 'audit', 'تدقيق المصروفات'),
                ]
              : []),
            item('/admin/customers', 'common.customers', 'customers'),
            item('/bonus', 'common.bonus', 'gift', 'Bonuses'),
          ],
        },
        {
          titleKey: 'navGroups.insights',
          items: [
            item('/admin/analytics', 'common.analytics', 'analytics'),
            item('/admin/staff-kpis', 'common.staffKpis', 'users', 'Team KPIs'),
            item('/admin/parts-estimator', 'common.partsEstimator', 'estimate', 'Price Estimator'),
            item('/admin/price-list', 'common.priceList', 'estimate', 'قائمة الأسعار'),
            item('/admin/price-comparison', 'common.priceComparison', 'compare', 'Price Comparison'),
            item('/admin/estimates', 'common.estimate', 'calculator', 'Estimate'),
          ],
        },
        { titleKey: 'mobile.catalog', items: CATALOG },
      ];
      if (sa) {
        groups.push({
          titleKey: 'navGroups.admin',
          items: [
            item('/admin/activity', 'activityLog.cardTitle', 'shield', 'سجل النشاطات'),
            item('/admin/master-data', 'common.masterData', 'database'),
            item('/admin/bot', 'mobile.pricingAgent', 'robot', 'وكيل التسعير'),
            item('/admin/assistant', 'mobile.adminAssistant', 'sparkle', 'المساعد الذكي'),
            item('/admin/pos', 'common.posDevices', 'card', 'أجهزة نقاط البيع'),
            item('/admin/locations', 'common.receivingLocations', 'bay'),
            item('/admin/labour-links', 'common.labourLinksAdmin', 'wrench'),
          ],
        });
      }
      return groups;
    }

    default:
      return [{ titleKey: null, items: [item('/', 'common.dashboard', 'dashboard'), item('/orders', 'common.orders', 'orders')] }];
  }
}

/** Flat lookup: the best (longest) nav entry for a path — used for the top bar title. */
export function navItemForPath(user: User | null | undefined, pathname: string): NavItem | null {
  let best: NavItem | null = null;
  for (const g of navGroupsFor(user)) {
    for (const it of g.items) {
      const hit = it.to === '/' ? pathname === '/' : pathname === it.to || pathname.startsWith(it.to + '/');
      if (hit && (!best || it.to.length > best.to.length)) best = it;
    }
  }
  return best;
}

/** The bottom tab bar: up to four destinations per role (+ «المزيد», always last). */
export interface TabItem {
  to: string;
  labelKey: string;
  icon: IconName;
  /** The raised gold «+» in the middle. */
  primary?: boolean;
  /** A live count on the icon (v2 workshops' «الطلبات»: orders waiting for them). */
  badge?: WsNavBadgeKind;
}

export function bottomTabsFor(user: User | null | undefined): TabItem[] {
  if (!user) return [];
  const home: TabItem = { to: '/', labelKey: 'bottomNav.home', icon: 'home' };
  const orders: TabItem = { to: '/orders', labelKey: 'bottomNav.orders', icon: 'orders' };
  const create: TabItem = { to: '/orders/create', labelKey: 'bottomNav.create', icon: 'plus', primary: true };
  if (isReadOnlyAdmin(user)) return [home, orders];
  switch (user.role) {
    case UserRole.WORKSHOP:
      if (isWorkshopV2(user)) {
        return workshopBottomTabs(user).map((i) => ({
          to: i.to, labelKey: i.labelKey, icon: i.icon, ...(i.primary ? { primary: true } : {}), ...(i.badge ? { badge: i.badge } : {}),
        }));
      }
      return [home, orders, create, { to: '/assistant', labelKey: 'bottomNav.assistant', icon: 'robot' }];
    case UserRole.AGENT:
      return [home, orders, create, { to: '/bonus', labelKey: 'bottomNav.bonus', icon: 'gift' }];
    case UserRole.SUPPLIER:
      return [home, orders, { to: '/bids', labelKey: 'common.myBids', icon: 'gavel' }];
    case UserRole.ADMIN:
    case UserRole.SUPER_ADMIN:
      return [home, orders, create, { to: '/admin/purchase-invoices', labelKey: 'bottomNav.invoices', icon: 'invoice' }];
    default:
      return [home, orders];
  }
}

/** Routes that belong to a customer holding a link — no app chrome at all. */
export const PUBLIC_PREFIXES = ['/login', '/feedback', '/quote', '/jc', '/pay', '/labour', '/d'];
export const isPublicPath = (pathname: string) =>
  PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
